import { useState } from "react";
import { BadgeCheck, MessageSquarePlus } from "lucide-react";
import { Stars } from "./Stars";
import { ReviewForm } from "./ReviewForm";
import { useProductReviews } from "./useReviews";
import { imgUrl } from "./useProducts";

const date = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export const Reviews = ({ product }) => {
  const { reviews, reload } = useProductReviews(product.id);
  const [writing, setWriting] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  if (reviews === null) return null;
  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

  return (
    <section data-testid="product-reviews" className="mt-8 border-t border-[#e3e2dc] pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a]">Reviews</p>
          <div className="flex items-center gap-2 mt-1">
            <Stars value={avg} size={15} testId="reviews-avg-stars" />
            <span data-testid="reviews-summary" className="text-sm">{reviews.length ? `${avg.toFixed(1)} · ${reviews.length} review${reviews.length > 1 ? "s" : ""}` : "No reviews yet"}</span>
          </div>
        </div>
        {!writing && (
          <button type="button" data-testid="review-open-form" onClick={() => setWriting(true)} className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[#e3e2dc] hover:border-[#0a0a0b] font-mono-d text-[10px] uppercase tracking-[0.2em] transition-colors">
            <MessageSquarePlus size={13} /> Write a review
          </button>
        )}
      </div>

      {writing && <div className="mt-4"><ReviewForm product={product} onPosted={reload} onCancel={() => setWriting(false)} /></div>}

      {reviews.length > 0 && (
        <ul data-testid="reviews-list" className="mt-5 space-y-5">
          {reviews.map((r) => (
            <li key={r.id} data-testid={`review-${r.id}`} className="border-b border-[#e3e2dc] pb-5 last:border-0 last:pb-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Stars value={r.rating} size={13} testId={`review-stars-${r.id}`} />
                <span className="text-sm font-semibold">{r.reviewer}</span>
                <span className="inline-flex items-center gap-1 font-mono-d text-[9px] uppercase tracking-[0.15em] text-[#007a3d]"><BadgeCheck size={12} /> Verified buyer</span>
                <span className="font-mono-d text-[9px] uppercase tracking-[0.15em] text-[#5f5e5a] ml-auto">{date(r.created_at)}</span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-[#0a0a0b]">{r.text}</p>
              {(r.size || r.color) && <p className="mt-1 font-mono-d text-[9px] uppercase tracking-[0.15em] text-[#5f5e5a]">{[r.color, r.size && r.size !== "ONE SIZE" ? `Size ${r.size}` : null].filter(Boolean).join(" · ")}</p>}
              {r.photos?.length > 0 && (
                <div className="mt-3 flex gap-2">
                  {r.photos.map((p) => (
                    <button key={p} type="button" data-testid="review-photo" onClick={() => setLightbox(p)} className="h-20 w-20 rounded-lg overflow-hidden border border-[#e3e2dc] hover:border-[#ff2a85] transition-colors">
                      <img src={imgUrl(p)} alt="Customer photo" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {lightbox && (
        <button type="button" data-testid="review-lightbox" onClick={() => setLightbox(null)} className="fixed inset-0 z-[80] bg-[#0a0a0b]/90 flex items-center justify-center p-6 cursor-zoom-out">
          <img src={imgUrl(lightbox)} alt="Customer photo" className="max-h-full max-w-full rounded-2xl object-contain" />
        </button>
      )}
    </section>
  );
};
